import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/axiosInstance";
import {
  ArrowLeft,
  Wifi,
  Car,
  Coffee,
  Utensils,
  Tv,
  Wind,
  ShowerHead,
  Bed,
  Users,
  Sparkles,
  CheckCircle,
  BedDouble,
  Award,
  Phone,
  Mail,
} from "lucide-react";
import FloatingContactButton from "../components/FloatingContactButton";
import BookingRequestForm from "../components/BookingRequestForm";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const getAmenityIcon = (amenity) => {
  const a = amenity.toLowerCase().trim();
  if (a.includes("wifi") || a.includes("wi-fi"))
    return <Wifi size={20} className="text-emerald-500" />;
  if (a.includes("parking") || a.includes("car"))
    return <Car size={20} className="text-emerald-500" />;
  if (a.includes("coffee") || a.includes("tea"))
    return <Coffee size={20} className="text-emerald-500" />;
  if (a.includes("tv") || a.includes("television"))
    return <Tv size={20} className="text-emerald-500" />;
  if (a.includes("ac") || a.includes("air condition"))
    return <Wind size={20} className="text-emerald-500" />;
  if (a.includes("bathroom") || a.includes("shower"))
    return <ShowerHead size={20} className="text-emerald-500" />;
  if (a.includes("bed")) return <Bed size={20} className="text-emerald-500" />;
  if (a.includes("restaurant") || a.includes("food") || a.includes("dining"))
    return <Utensils size={20} className="text-emerald-500" />;
  if (a.includes("people") || a.includes("guest") || a.includes("capacity"))
    return <Users size={20} className="text-emerald-500" />;
  return (
    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-emerald-500 to-lime-500 flex items-center justify-center">
      <CheckCircle className="w-3 h-3 text-white" />
    </div>
  );
};

const GuestRoomDetails = () => {
  const { id } = useParams();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveImageIndex(0);
    const fetchRoom = async () => {
      try {
        const res = await api.get(`/guest-rooms/get/${id}`);
        setRoom(res.data);
      } catch (err) {
        console.error(err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50 flex items-center justify-center">
        <div className="inline-flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading room details...</span>
        </div>
      </div>
    );
  }

  if (notFound || !room) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50 flex items-center justify-center">
        <div className="text-center">
          <BedDouble className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Room not found
          </h3>
          <Link
            to="/guest-room"
            className="text-emerald-600 font-semibold hover:underline"
          >
            ← Back to all rooms
          </Link>
        </div>
        <FloatingContactButton />
      </div>
    );
  }

  const images = room.imageUrls || [];
  const amenities = room.amenities
    ? room.amenities
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean)
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Link
          to="/guest-room"
          className="inline-flex items-center gap-2 text-emerald-700 font-semibold mb-6 hover:gap-3 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to all rooms
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Tile Gallery */}
          <div className="space-y-4">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              {images.length > 0 ? (
                <img
                  src={images[activeImageIndex]}
                  alt={room.title}
                  onClick={() => setLightboxOpen(true)}
                  className="w-full h-80 md:h-96 object-cover cursor-zoom-in"
                />
              ) : (
                <div className="w-full h-80 md:h-96 bg-gradient-to-br from-emerald-100 to-lime-100 flex items-center justify-center">
                  <BedDouble className="w-16 h-16 text-emerald-400" />
                </div>
              )}

              <div className="absolute top-4 right-4">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold shadow ${
                    room.available
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-500 text-white"
                  }`}
                >
                  {room.available ? "Available" : "Unavailable"}
                </span>
              </div>
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                      index === activeImageIndex
                        ? "border-emerald-500 shadow-lg"
                        : "border-transparent hover:border-emerald-300"
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${room.title} ${index + 1}`}
                      className="w-full h-16 sm:h-20 object-cover"
                    />
                    {index === activeImageIndex && (
                      <div className="absolute inset-0 bg-emerald-500/20"></div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Room Details */}
          <div className="space-y-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-xl border border-emerald-100">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {room.title}
                  </h1>
                  {room.description && (
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {room.description}
                    </p>
                  )}
                </div>

                {room.pricePerNight ? (
                  <div className="text-right flex-shrink-0">
                    <div className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-lime-600 bg-clip-text text-transparent">
                      ৳{room.pricePerNight}
                    </div>
                    <div className="text-gray-500 text-sm">per night</div>
                  </div>
                ) : null}
              </div>

              {room.capacity && (
                <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl bg-gradient-to-br from-emerald-50 to-lime-50">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-gray-700">
                    Sleeps {room.capacity}
                  </span>
                </div>
              )}

              {amenities.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-500" />
                    Amenities
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {amenities.map((amenity, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-50 to-lime-50 flex items-center justify-center">
                          {getAmenityIcon(amenity)}
                        </div>
                        <span className="font-medium text-gray-700">
                          {amenity}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() =>
                  document
                    .getElementById("booking-form")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-bold hover:from-emerald-600 hover:to-lime-600 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
              >
                Book This Room
              </button>
            </div>
          </div>
        </div>

        {/* Booking Request Form */}
        <div id="booking-form" className="mt-12 scroll-mt-24">
          <BookingRequestForm room={room} />
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-emerald-500 to-lime-500 rounded-2xl p-8 md:p-12 text-white text-center shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center gap-3 mb-4">
              <Award className="w-8 h-8" />
              <Award className="w-8 h-8" />
              <Award className="w-8 h-8" />
            </div>

            <h3 className="text-2xl md:text-3xl font-bold mb-6">
              Premium Guest Experience Awaits
            </h3>

            <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
              Book your stay at RGCC and enjoy world-class hospitality
              combined with championship golf
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:01769660127"
                className="inline-flex items-center justify-center gap-3 px-8 py-3 rounded-xl font-bold bg-white text-emerald-600 hover:bg-emerald-50 hover:scale-105 transition-all duration-300 shadow-lg"
              >
                <Phone className="w-5 h-5" />
                Call Now: 01769660127
              </a>

              <a
                href="mailto:rgcc66div@gmail.com"
                className="inline-flex items-center justify-center gap-3 px-8 py-3 rounded-xl font-bold bg-emerald-700 text-white hover:bg-emerald-800 hover:scale-105 transition-all duration-300 shadow-lg border border-emerald-600"
              >
                <Mail className="w-5 h-5" />
                Email: rgcc66div@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <FloatingContactButton />

      {images.length > 0 && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[{ src: images[activeImageIndex] }]}
          controller={{ closeOnBackdropClick: true }}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
        />
      )}
    </div>
  );
};

export default GuestRoomDetails;
